/**
 * Fiche d'un GRV, à onglets (`07-frontend-react.md` §4).
 *
 * Chaque onglet est un écran de domaine à part entière, importé depuis sa propre feature — pas de
 * duplication de logique de requête : la fiche de vie de l'onglet "maintenance" est exactement le
 * composant utilisé par le module maintenance transversal, filtré sur ce GRV.
 */
import { Suspense, lazy } from "react";
import { NavLink, Route, Routes, useParams } from "react-router-dom";

import { OngletFicheDeVie } from "../maintenance/OngletFicheDeVie";
import { OngletDocuments } from "../documents/OngletDocuments";
import { OngletMouvements } from "../mouvements/OngletMouvements";
import { OngletAction } from "./OngletAction";
import { OngletInformations } from "./OngletInformations";
import { OngletMouvementsDetailles } from "./OngletMouvementsDetailles";
import { useFicheGrv } from "./requetes";
import { useIdentite } from "../../shared/auth/contexte";
import { Chargement, Erreur } from "../../shared/composants/Etats";

// `maplibre-gl` pèse à lui seul plus que le reste de l'application (`App.tsx`, même raison pour
// `EcranCarte` et `EcranGeozones`) : le charger paresseusement évite d'imposer son poids à qui
// n'ouvre jamais l'onglet Carte d'une fiche GRV.
const OngletCarteGrv = lazy(() =>
  import("./OngletCarteGrv").then((module) => ({ default: module.OngletCarteGrv })),
);

export function EcranFicheGrv() {
  const { numero } = useParams<{ numero: string }>();
  const identite = useIdentite();
  const requete = useFicheGrv(numero);
  const peutAdministrer = identite.profil !== "air-tahiti" && identite.profil !== "support";
  const voitMaintenance = identite.profil !== "air-tahiti";
  const voitDocuments = identite.profil !== "support";
  // Suppression d'un document réservée à l'administrateur (`docs/DECISIONS.md` D84), à la
  // différence du dépôt (ouvert à l'opérationnel aussi, `peutAdministrer`).
  const peutSupprimerDocument = identite.profil === "admin-totalenergies";

  if (requete.isPending) return <Chargement libelle="Chargement de la fiche…" />;
  if (requete.isError) {
    return <Erreur erreur={requete.error} reessayer={() => void requete.refetch()} />;
  }

  const fiche = requete.data.data;
  const base = `/grv/${encodeURIComponent(fiche.numero_grv)}`;

  return (
    <section className="fiche-grv">
      <div className="fiche-grv__entete">
        <h1>{fiche.numero_grv}</h1>
        <span className="badge">{fiche.statut_operationnel}</span>
      </div>

      <nav className="fiche-grv__onglets" aria-label="Sections de la fiche">
        <NavLink to={`${base}/informations`}>Informations</NavLink>
        <NavLink to={`${base}/carte`}>Carte</NavLink>
        <NavLink to={`${base}/mouvements`}>Mouvements</NavLink>
        <NavLink to={`${base}/mouvements-detailles`}>Mouvements détaillés</NavLink>
        {voitMaintenance && <NavLink to={`${base}/fiche-de-vie`}>Fiche de vie</NavLink>}
        {peutAdministrer && <NavLink to={`${base}/action`}>Action</NavLink>}
        {voitDocuments && <NavLink to={`${base}/documents`}>Documents</NavLink>}
      </nav>

      <Routes>
        <Route
          path="informations"
          element={<OngletInformations fiche={fiche} peutModifier={peutAdministrer} />}
        />
        <Route
          path="carte"
          element={
            <Suspense fallback={<Chargement libelle="Chargement de la carte…" />}>
              <OngletCarteGrv numero={fiche.numero_grv} />
            </Suspense>
          }
        />
        <Route path="mouvements" element={<OngletMouvements numero={fiche.numero_grv} />} />
        <Route
          path="mouvements-detailles"
          element={<OngletMouvementsDetailles numero={fiche.numero_grv} />}
        />
        {voitMaintenance && (
          <Route path="fiche-de-vie" element={<OngletFicheDeVie numero={fiche.numero_grv} />} />
        )}
        {peutAdministrer && <Route path="action" element={<OngletAction fiche={fiche} />} />}
        {voitDocuments && (
          <Route
            path="documents"
            element={
              <OngletDocuments
                numero={fiche.numero_grv}
                peutDeposer={peutAdministrer}
                peutSupprimer={peutSupprimerDocument}
                voitChecklist={voitMaintenance}
              />
            }
          />
        )}
        <Route
          path="*"
          element={<OngletInformations fiche={fiche} peutModifier={peutAdministrer} />}
        />
      </Routes>
    </section>
  );
}
